import { Icon } from '../Icon';

interface MediaPlaceholderProps {
    /** Type de média attendu — contrôle l'icône et le ratio par défaut. */
    kind?: 'screenshot' | 'video' | 'illustration' | 'diagram' | 'photo' | 'animation';
    /** Ratio d'affichage (ex. "16:9", "4:3", "1:1"). Défaut : 16:9. */
    ratio?: string;
    /** Titre court du média. */
    title?: string;
    /** Description recommandée (primaire). */
    recommended: string;
    /** 1re alternative. */
    alt1?: string;
    /** 2e alternative. */
    alt2?: string;
    /** Notes supplémentaires (outils, cadrage, accessibilité). */
    notes?: string;
}

const KIND_ICON: Record<NonNullable<MediaPlaceholderProps['kind']>, 'image' | 'video' | 'camera' | 'layers'> = {
    screenshot: 'image',
    video: 'video',
    illustration: 'image',
    diagram: 'layers',
    photo: 'camera',
    animation: 'video',
};

const KIND_LABEL: Record<NonNullable<MediaPlaceholderProps['kind']>, string> = {
    screenshot: 'Capture d\'écran',
    video: 'Vidéo',
    illustration: 'Illustration',
    diagram: 'Schéma',
    photo: 'Photo',
    animation: 'Animation',
};

/**
 * Placeholder typé pour un média à intégrer plus tard (capture, vidéo,
 * illustration…). Affiche un encart visuel stylé avec :
 *   - une recommandation « Recommandé »
 *   - jusqu'à 2 alternatives
 *
 * Les auteurs peuvent ainsi rédiger la doc sans attendre que les visuels
 * soient produits, et l'équipe média sait exactement quoi livrer.
 */
export function MediaPlaceholder({
    kind = 'screenshot',
    ratio = '16:9',
    title,
    recommended,
    alt1,
    alt2,
    notes,
}: MediaPlaceholderProps) {
    const iconName = KIND_ICON[kind];
    const [rw, rh] = parseRatio(ratio);
    const paddingTop = `${(rh / rw) * 100}%`;

    return (
        <figure className={`media-ph media-ph-${kind}`}>
            <div className="media-ph-canvas" style={{ paddingTop }}>
                <div className="media-ph-canvas-inner">
                    <div className="media-ph-badge">
                        <Icon name={iconName} size={14} />
                        <span>{KIND_LABEL[kind]}</span>
                        <span className="media-ph-ratio">{ratio}</span>
                    </div>
                    {title && <div className="media-ph-title">{title}</div>}
                </div>
            </div>

            <figcaption className="media-ph-caption">
                <div className="media-ph-opt media-ph-opt-main">
                    <span className="media-ph-opt-label">1. Recommandé</span>
                    <p>{recommended}</p>
                </div>
                {alt1 && (
                    <div className="media-ph-opt">
                        <span className="media-ph-opt-label">2. Alternative</span>
                        <p>{alt1}</p>
                    </div>
                )}
                {alt2 && (
                    <div className="media-ph-opt">
                        <span className="media-ph-opt-label">3. Alternative</span>
                        <p>{alt2}</p>
                    </div>
                )}
                {notes && (
                    <div className="media-ph-notes">
                        <Icon name="info" size={12} />
                        <span>{notes}</span>
                    </div>
                )}
            </figcaption>
        </figure>
    );
}

function parseRatio(ratio: string): [number, number] {
    const [a, b] = ratio.split(':').map((n) => parseFloat(n));
    if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) {
        return [16, 9];
    }
    return [a, b];
}
